import {
  AlertTriangle,
  Button,
  Eye,
  Input,
  Modal,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
// MED-1479: the design system's own SearchAutocomplete glues a hardcoded
// English "(min. N characters)" onto whatever placeholder it is given, which
// no prop can turn off. This repo's copy takes the same props and builds that
// hint from a translation instead.
import {
  SearchableEntity,
  SearchAutocomplete,
} from "../../components/molecules/SearchAutocomplete/SearchAutocomplete";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { facilitiesApi } from "../../services/facilities.api";
import { licenceApi } from "../../services/licence.api";
import { promotionApi } from "../../services/promotion.api";
import { useApiFormErrors } from "../../hooks/useApiFormErrors";
import {
  FREE_TRIAL_PROMOTION_CODE,
  PromotionCode,
  PromotionFacilityAssignment,
  PromotionTranslations,
} from "../../types/promotion.definition";
import { formatDateTime } from "../../utils/date-format-utils";
import { useAppDispatch } from "../../store/hooks";
import { clearGlobalError } from "../../store/reducers/errorSlice";
import { textDirection } from "../../utils/text-direction-utils";

interface FacilitySearchEntity extends SearchableEntity {
  id: string;
  name: string;
}

async function searchFacilitiesForAssign(
  keyword: string
): Promise<FacilitySearchEntity[]> {
  const results = await facilitiesApi.searchFacilities(keyword);
  return results.map(f => ({ ...f, id: String(f.id) }));
}

type LangKey = "en" | "fr" | "ar";

const LANGS: { key: LangKey; label: string }[] = [
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
  { key: "ar", label: "العربية" },
];

const ALL_FACILITIES_VALUE = "ALL";

/** The create-promotion-code form's own field names. */
type PromotionCodeField =
  | "promotionCode"
  | "rebate"
  | "translationEn"
  | "translationFr"
  | "translationAr";

/**
 * MED-1532: the API rejects a missing label under its nested payload path
 * (`translations.ar`), which this form used to surface verbatim as
 * "translations.ar: must not be blank". These are the form's own fields for
 * those paths, and the tab each one belongs to.
 */
const TRANSLATION_FIELD: Record<LangKey, PromotionCodeField> = {
  en: "translationEn",
  fr: "translationFr",
  ar: "translationAr",
};

function emptyTranslations(): PromotionTranslations {
  return { en: "", fr: "", ar: "" };
}

function rebateLabel(
  promo: PromotionCode,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (promo.promotionCode === FREE_TRIAL_PROMOTION_CODE) {
    return t("admin.licencePromotions.freeTrial");
  }
  return t("admin.licencePromotions.rebatePercent", {
    rebate: promo.rebate ?? 0,
  });
}

export const LicencePromotionsManagement: React.FC = () => {
  const { t } = useTranslation();

  /**
   * MED-1540: the facilities "All Facilities" can reach — every facility that
   * holds a licence, which is exactly what the API accepts (assigning to a
   * facility with no licence is refused with PROMOTION_FACILITY_HAS_NO_LICENCE).
   *
   * This used to be the first page of the whole directory search, capped at 100
   * by PaginationSettingVM. On 2 567 facilities that meant "all" was 100
   * arbitrary rows — mostly facilities with no licence, which the API refuses one
   * by one, while licensed facilities past the first page were never reached at
   * all. GET /licences/facilities returns every licence with no pagination.
   */
  const [licensedFacilityIds, setLicensedFacilityIds] = useState<number[]>([]);
  const [licensedFacilitiesLoading, setLicensedFacilitiesLoading] =
    useState(true);

  const [promotions, setPromotions] = useState<PromotionCode[]>([]);
  const [loading, setLoading] = useState(true);

  // Create Promotion Code modal
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [codeValue, setCodeValue] = useState("");
  const [rebateValue, setRebateValue] = useState("");
  // MED-1477: the API names the field it rejected (`promotionCode`) or rejects
  // the rebate by code; both belong on their own input, not in a generic modal.
  const codeErrors = useApiFormErrors<PromotionCodeField>({
    fields: {
      promotionCode: "promotionCode",
      rebate: "rebate",
      // The API names these by their nested payload path (MED-1532).
      "translations.en": "translationEn",
      "translations.fr": "translationFr",
      "translations.ar": "translationAr",
    },
    codes: {
      PROMOTION_REBATE_INVALID: "rebate",
      PROMOTION_CODE_INVALID: "promotionCode",
      PROMOTION_CODE_ALREADY_EXISTS: "promotionCode",
    },
    // Name the label and its language in words, so the message says which tab
    // to go to rather than repeating the payload path.
    fieldMessage: (field, apiMessage) => {
      const lang = LANGS.find(l => TRANSLATION_FIELD[l.key] === field);
      if (!lang) return undefined;
      return t("admin.licencePromotions.code.form.translationFieldError", {
        lang: t(`common.languages.${lang.key}`),
        message: apiMessage || t("apiErrors.fieldRejected"),
      });
    },
  });
  const [codeTranslations, setCodeTranslations] =
    useState<PromotionTranslations>(emptyTranslations());
  const [activeLang, setActiveLang] = useState<LangKey>("fr");
  const [savingCode, setSavingCode] = useState(false);

  // Create / Edit Promotion Period modal
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [periodEditTarget, setPeriodEditTarget] =
    useState<PromotionCode | null>(null);
  const [periodCode, setPeriodCode] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [savingPeriod, setSavingPeriod] = useState(false);

  // Details modal
  const [detailsPromotion, setDetailsPromotion] =
    useState<PromotionCode | null>(null);
  const [assignedFacilities, setAssignedFacilities] = useState<
    PromotionFacilityAssignment[]
  >([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [unassigningFacilityId, setUnassigningFacilityId] = useState<
    number | null
  >(null);

  const dispatch = useAppDispatch();

  /**
   * MED-1543: the assign dialog reported a refusal twice — once in its own
   * box under the form, and once in the generic error modal the axios
   * interceptor raises for every API error. This dialog handles its own
   * failures, so it takes the modal down as it does, which is the same
   * contract `useApiFormErrors` already applies to the forms that use it
   * (MED-1476).
   */
  const showAssignError = useCallback(
    (message: string) => {
      setAssignError(message);
      dispatch(clearGlobalError());
    },
    [dispatch]
  );

  // Assign modal
  const [assignTarget, setAssignTarget] = useState<PromotionCode | null>(null);
  const [assignFacilityValue, setAssignFacilityValue] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [confirmAssignAll, setConfirmAssignAll] = useState(false);

  // Delete (whole promotion code) confirmation
  const [deleteTarget, setDeleteTarget] = useState<PromotionCode | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadPromotions = () => {
    setLoading(true);
    promotionApi
      .getPromotionCodes()
      .then(setPromotions)
      .catch(() => toast.error(t("admin.licencePromotions.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    licenceApi
      .getAllFacilityLicences()
      .then(licences => {
        const ids = licences
          .map(licence => licence.facilityId)
          .filter((id): id is number => id !== null);
        setLicensedFacilityIds([...new Set(ids)]);
      })
      .catch(() => setLicensedFacilityIds([]))
      .finally(() => setLicensedFacilitiesLoading(false));
  }, []);

  // ── Create Promotion Code ────────────────────────────────────────────────
  const openCodeModal = () => {
    setCodeValue("");
    setRebateValue("");
    setCodeTranslations(emptyTranslations());
    setActiveLang("fr");
    codeErrors.clear();
    setIsCodeModalOpen(true);
  };

  const handleSaveCode = async () => {
    if (!codeValue.trim()) return;
    setSavingCode(true);
    codeErrors.clear();
    try {
      await promotionApi.createPromotionCode({
        promotionCode: codeValue.trim().toUpperCase(),
        rebate: rebateValue.trim() === "" ? null : Number(rebateValue),
        translations: codeTranslations,
      });
      setIsCodeModalOpen(false);
      toast.success(t("admin.licencePromotions.code.createSuccess"));
      loadPromotions();
    } catch (error) {
      // MED-1477: the rejection is specific — VALIDATION_FAILED names
      // `promotionCode`, PROMOTION_REBATE_INVALID explains the rebate range.
      // Show it on the field it belongs to; the toast is only for a rejection
      // that said nothing at all.
      if (!codeErrors.handleError(error).handled) {
        toast.error(t("admin.licencePromotions.code.createError"));
      }
    } finally {
      setSavingCode(false);
    }
  };

  // ── Create / Edit Promotion Period ───────────────────────────────────────
  const openCreatePeriodModal = () => {
    setPeriodEditTarget(null);
    setPeriodCode("");
    setPeriodStart("");
    setPeriodEnd("");
    setIsPeriodModalOpen(true);
  };

  const openEditPeriodModal = (promo: PromotionCode) => {
    setPeriodEditTarget(promo);
    setPeriodCode(promo.promotionCode);
    setPeriodStart(promo.startDate ?? "");
    setPeriodEnd(promo.endDate ?? "");
    setIsPeriodModalOpen(true);
  };

  const isPeriodValid =
    !!periodCode.trim() &&
    !!periodStart &&
    !!periodEnd &&
    periodStart <= periodEnd;

  const handleSavePeriod = async () => {
    if (!isPeriodValid) return;
    setSavingPeriod(true);
    try {
      // A code with no validity period yet has no `promotionId` (see the
      // PromotionCode.promotionId doc comment), so editing it must fall
      // through to create-period even though periodEditTarget is set —
      // branch on promotionId, not on periodEditTarget truthiness.
      if (periodEditTarget && periodEditTarget.promotionId !== undefined) {
        await promotionApi.updatePromotionPeriod(periodEditTarget.promotionId, {
          startDate: periodStart,
          endDate: periodEnd,
        });
      } else {
        await promotionApi.createPromotionPeriod({
          promotionCodeKey: periodCode.trim(),
          startDate: periodStart,
          endDate: periodEnd,
        });
      }
      setIsPeriodModalOpen(false);
      toast.success(t("admin.licencePromotions.period.saveSuccess"));
      loadPromotions();
    } catch {
      toast.error(t("admin.licencePromotions.period.saveError"));
    } finally {
      setSavingPeriod(false);
    }
  };

  // ── Details ───────────────────────────────────────────────────────────────
  const openDetails = (promo: PromotionCode) => {
    setDetailsPromotion(promo);
    setAssignedFacilities([]);
    if (promo.promotionId !== undefined) {
      setDetailsLoading(true);
      promotionApi
        .getPromotionFacilities(promo.promotionId)
        .then(setAssignedFacilities)
        .catch(() => setAssignedFacilities([]))
        .finally(() => setDetailsLoading(false));
    }
  };

  const handleUnassignFromDetails = async (facilityId: number) => {
    if (!detailsPromotion || detailsPromotion.promotionId === undefined) return;
    setUnassigningFacilityId(facilityId);
    try {
      await promotionApi.unassignPromotion({
        promotionKey: detailsPromotion.promotionId,
        facilityId,
      });
      setAssignedFacilities(prev =>
        prev.filter(a => a.facilityId !== facilityId)
      );
      // MED-1478: the table's "Établissements" column reads facilitiesCount,
      // which is only refreshed by loadPromotions(). Without this the counter
      // kept its old value until the page was reloaded.
      setPromotions(prev =>
        prev.map(p =>
          p.promotionId === detailsPromotion.promotionId
            ? { ...p, facilitiesCount: Math.max(0, p.facilitiesCount - 1) }
            : p
        )
      );
      toast.success(t("admin.licencePromotions.details.unassignSuccess"));
    } catch {
      toast.error(t("admin.licencePromotions.details.unassignError"));
    } finally {
      setUnassigningFacilityId(null);
    }
  };

  // ── Assign ───────────────────────────────────────────────────────────────
  const openAssignModal = (promo: PromotionCode) => {
    setAssignTarget(promo);
    setAssignFacilityValue("");
    setAssignError(null);
  };

  const closeAssignModal = () => {
    setAssignTarget(null);
    setAssignFacilityValue("");
    setAssignError(null);
    setConfirmAssignAll(false);
  };

  const performAssign = async (facilityId: number): Promise<boolean> => {
    if (!assignTarget || assignTarget.promotionId === undefined) return false;
    try {
      await promotionApi.assignPromotion({
        promotionKey: assignTarget.promotionId,
        facilityId,
      });
      return true;
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (
        code === "PROMOTION_ALREADY_ASSIGNED_TO_FACILITY" ||
        code === "PROMOTION_FACILITY_HAS_NO_LICENCE"
      ) {
        throw err;
      }
      return false;
    }
  };

  const handleAssignConfirm = async () => {
    if (!assignTarget || !assignFacilityValue) return;
    if (assignTarget.promotionId === undefined) {
      // No validity period exists for this code yet — assigning is
      // meaningless (and was previously failing silently while still
      // showing a success toast). Surface this clearly instead.
      showAssignError(t("admin.licencePromotions.assign.noPeriodError"));
      return;
    }
    setAssignError(null);

    if (assignFacilityValue === ALL_FACILITIES_VALUE) {
      setAssigning(true);
      let successCount = 0;
      let skippedCount = 0;
      for (const facilityId of licensedFacilityIds) {
        try {
          const ok = await performAssign(facilityId);
          if (ok) {
            successCount += 1;
          } else {
            skippedCount += 1;
          }
        } catch {
          // Already assigned to this promotion, or the licence went away between
          // the list and the call — neither is a reason to stop the run.
          skippedCount += 1;
        }
      }
      setAssigning(false);
      if (successCount === 0) {
        // MED-1540: every facility was refused or already assigned. Saying
        // nothing but "failed" hid the common case — the promotion was already
        // on all of them.
        showAssignError(
          skippedCount > 0
            ? t("admin.licencePromotions.assign.bulkNoneLeft", {
                count: skippedCount,
              })
            : t("admin.licencePromotions.assign.genericError")
        );
        return;
      }
      toast.success(
        skippedCount > 0
          ? t("admin.licencePromotions.assign.bulkPartialSuccess", {
              count: successCount,
              skipped: skippedCount,
            })
          : t("admin.licencePromotions.assign.bulkSuccess", {
              count: successCount,
            })
      );
      closeAssignModal();
      loadPromotions();
      return;
    }

    setAssigning(true);
    try {
      const ok = await performAssign(Number(assignFacilityValue));
      if (!ok) {
        showAssignError(t("admin.licencePromotions.assign.genericError"));
        return;
      }
      toast.success(t("admin.licencePromotions.assign.success"));
      closeAssignModal();
      loadPromotions();
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === "PROMOTION_ALREADY_ASSIGNED_TO_FACILITY") {
        showAssignError(t("admin.licencePromotions.assign.duplicateError"));
      } else if (code === "PROMOTION_FACILITY_HAS_NO_LICENCE") {
        showAssignError(t("admin.licencePromotions.assign.noLicenceError"));
      } else {
        showAssignError(t("admin.licencePromotions.assign.genericError"));
      }
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignSubmit = () => {
    if (assignFacilityValue === ALL_FACILITIES_VALUE) {
      setConfirmAssignAll(true);
      return;
    }
    void handleAssignConfirm();
  };

  // ── Delete (whole promotion code) ───────────────────────────────────────
  // MED-1441: this used to fetch the assigned facilities and unassign each one,
  // which left the code itself in the list. Call the real delete endpoint.
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    try {
      await promotionApi.deletePromotionCode(deleteTarget.id);
      toast.success(t("admin.licencePromotions.delete.success"));
      loadPromotions();
    } catch {
      toast.error(t("admin.licencePromotions.delete.error"));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const periodCodeOptions = Array.from(
    new Set(promotions.map(p => p.promotionCode))
  ).map(code => ({ value: code, label: code }));

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex items-center justify-between page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.licencePromotions.title")}
          </h2>
          <p className="text-neutral-600">
            {t("admin.licencePromotions.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={loadPromotions}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button
            variant="outline"
            onClick={openCreatePeriodModal}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.licencePromotions.period.addNew")}</span>
          </Button>
          <Button
            onClick={openCodeModal}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.licencePromotions.code.addNew")}</span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="page-section">
        {loading ? (
          <div className="py-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : promotions.length === 0 ? (
          <div className="py-12 text-center">
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.licencePromotions.empty")}
            </h3>
          </div>
        ) : (
          <div className="overflow-hidden border rounded-lg border-neutral-200">
            <table className="w-full text-sm">
              <thead className="border-b bg-neutral-50 border-neutral-200">
                <tr>
                  <th className="px-4 py-3 font-medium text-left text-neutral-600">
                    {t("admin.licencePromotions.table.code")}
                  </th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-600">
                    {t("admin.licencePromotions.table.rebate")}
                  </th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-600">
                    {t("admin.licencePromotions.table.startDate")}
                  </th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-600">
                    {t("admin.licencePromotions.table.endDate")}
                  </th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-600">
                    {t("admin.licencePromotions.table.facilities")}
                  </th>
                  <th className="px-4 py-3 font-medium text-right text-neutral-600">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {promotions.map(promo => (
                  <tr
                    key={promo.promotionCode}
                    className="transition-colors hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                        {promo.promotionCode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {rebateLabel(promo, t)}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {formatDateTime(promo.startDate)}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {formatDateTime(promo.endDate)}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {promo.facilitiesCount ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => openDetails(promo)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                          title={t("common.view")}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditPeriodModal(promo)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                          title={t("common.edit")}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openAssignModal(promo)}
                          disabled={promo.promotionId === undefined}
                          className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-neutral-600"
                          title={
                            promo.promotionId === undefined
                              ? t(
                                  "admin.licencePromotions.assign.noPeriodError"
                                )
                              : t("admin.licencePromotions.assign.title")
                          }
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(promo)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors"
                          title={t("common.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Promotion Code Modal */}
      <Modal
        isOpen={isCodeModalOpen}
        onClose={() => setIsCodeModalOpen(false)}
        size="md"
        title={t("admin.licencePromotions.code.addNew")}
      >
        <div className="space-y-5">
          {codeErrors.formMessage && (
            <div
              role="alert"
              className="p-3 text-sm rounded-md border bg-danger-50 border-danger-200 text-danger-700"
            >
              {codeErrors.formMessage}
            </div>
          )}

          <div>
            <label
              htmlFor="promotion-code-value"
              className="block mb-1 text-sm font-medium text-neutral-700"
            >
              {t("admin.licencePromotions.code.form.code")}
            </label>
            <Input
              id="promotion-code-value"
              size="sm"
              value={codeValue}
              onChange={e => {
                setCodeValue(e.target.value.toUpperCase());
                codeErrors.clearField("promotionCode");
              }}
              placeholder={t(
                "admin.licencePromotions.code.form.codePlaceholder"
              )}
              aria-invalid={!!codeErrors.fieldErrors.promotionCode}
              aria-describedby={
                codeErrors.fieldErrors.promotionCode
                  ? "promotion-code-value-error"
                  : undefined
              }
            />
            {codeErrors.fieldErrors.promotionCode && (
              <p
                id="promotion-code-value-error"
                role="alert"
                className="mt-1 text-xs text-danger-600"
              >
                {codeErrors.fieldErrors.promotionCode}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="promotion-code-rebate"
              className="block mb-1 text-sm font-medium text-neutral-700"
            >
              {t("admin.licencePromotions.code.form.rebate")}
            </label>
            <Input
              id="promotion-code-rebate"
              size="sm"
              type="number"
              min={0}
              max={100}
              value={rebateValue}
              onChange={e => {
                setRebateValue(e.target.value);
                codeErrors.clearField("rebate");
              }}
              placeholder={t(
                "admin.licencePromotions.code.form.rebatePlaceholder"
              )}
              aria-invalid={!!codeErrors.fieldErrors.rebate}
              aria-describedby={
                codeErrors.fieldErrors.rebate
                  ? "promotion-code-rebate-error"
                  : undefined
              }
            />
            {codeErrors.fieldErrors.rebate && (
              <p
                id="promotion-code-rebate-error"
                role="alert"
                className="mt-1 text-xs text-danger-600"
              >
                {codeErrors.fieldErrors.rebate}
              </p>
            )}
            <p className="mt-1 text-xs text-neutral-400">
              {t("admin.licencePromotions.code.form.rebateHint")}
            </p>
          </div>

          <div>
            <div className="flex items-center mb-4 border-b border-neutral-200">
              {LANGS.map(({ key, label: langLabel }) => {
                const langHasError =
                  !!codeErrors.fieldErrors[TRANSLATION_FIELD[key]];
                return (
                  <button
                    key={key}
                    onClick={() => setActiveLang(key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                      langHasError
                        ? "border-danger-500 text-danger-600"
                        : activeLang === key
                          ? "border-ews-primary text-ews-primary"
                          : "border-transparent text-neutral-600 hover:text-neutral-900"
                    }`}
                  >
                    {langLabel}
                  </button>
                );
              })}
            </div>
            <label className="block mb-1 text-sm font-medium text-neutral-700">
              {t("admin.licencePromotions.code.form.translation")}
            </label>
            <Input
              size="sm"
              dir={textDirection(activeLang)}
              value={codeTranslations[activeLang]}
              onChange={e => {
                setCodeTranslations(prev => ({
                  ...prev,
                  [activeLang]: e.target.value,
                }));
                codeErrors.clearField(TRANSLATION_FIELD[activeLang]);
              }}
              placeholder={t(
                "admin.licencePromotions.code.form.translationPlaceholder"
              )}
              aria-invalid={
                !!codeErrors.fieldErrors[TRANSLATION_FIELD[activeLang]]
              }
            />
            {/* Every label error, not only the active tab's: each names its own
                language, so a rejection on a tab the user is not looking at is
                still visible and still says where to go (MED-1532). */}
            {LANGS.map(({ key }) => {
              const message = codeErrors.fieldErrors[TRANSLATION_FIELD[key]];
              return message ? (
                <p
                  key={key}
                  role="alert"
                  className="mt-1 text-xs text-danger-600"
                >
                  {message}
                </p>
              ) : null;
            })}
          </div>

          <div className="flex justify-end pt-2 space-x-3 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={() => setIsCodeModalOpen(false)}
              disabled={savingCode}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSaveCode}
              loading={savingCode}
              disabled={savingCode || !codeValue.trim()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create / Edit Promotion Period Modal */}
      <Modal
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        size={"lg"}
        title={
          periodEditTarget && periodEditTarget.promotionId !== undefined
            ? t("admin.licencePromotions.period.editTitle")
            : t("admin.licencePromotions.period.addNew")
        }
      >
        <div className="space-y-4">
          {periodEditTarget ? (
            <div>
              <label className="block mb-1 text-sm font-medium text-neutral-700">
                {t("admin.licencePromotions.period.form.code")}
              </label>
              <Input size="sm" value={periodCode} disabled readOnly />
            </div>
          ) : (
            <Select
              label={t("admin.licencePromotions.period.form.code")}
              value={periodCode}
              onChange={value => setPeriodCode(value as string)}
              options={periodCodeOptions}
              placeholder={t(
                "admin.licencePromotions.period.form.codePlaceholder"
              )}
            />
          )}

          <Input
            type="date"
            size="sm"
            label={t("admin.licencePromotions.period.form.startDate")}
            value={periodStart}
            onChange={e => setPeriodStart(e.target.value)}
          />
          <Input
            type="date"
            size="sm"
            label={t("admin.licencePromotions.period.form.endDate")}
            value={periodEnd}
            onChange={e => setPeriodEnd(e.target.value)}
          />
          {periodStart && periodEnd && periodStart > periodEnd && (
            <p className="text-sm text-danger-600">
              {t("admin.licencePromotions.period.form.dateRangeInvalid")}
            </p>
          )}

          <div className="flex justify-end pt-2 space-x-3 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={() => setIsPeriodModalOpen(false)}
              disabled={savingPeriod}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSavePeriod}
              loading={savingPeriod}
              disabled={savingPeriod || !isPeriodValid}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={detailsPromotion !== null}
        onClose={() => setDetailsPromotion(null)}
        size="lg"
        title={t("admin.licencePromotions.details.title")}
      >
        {detailsPromotion && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-semibold uppercase text-neutral-400">
                  {t("admin.licencePromotions.table.code")}
                </p>
                <p className="font-mono">{detailsPromotion.promotionCode}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-neutral-400">
                  {t("admin.licencePromotions.table.rebate")}
                </p>
                <p>{rebateLabel(detailsPromotion, t)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-neutral-400">
                  {t("admin.licencePromotions.table.startDate")}
                </p>
                <p>{formatDateTime(detailsPromotion.startDate)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-neutral-400">
                  {t("admin.licencePromotions.table.endDate")}
                </p>
                <p>{formatDateTime(detailsPromotion.endDate)}</p>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-neutral-400">
                {t("admin.licencePromotions.details.translations")}
              </p>
              <div className="space-y-1 text-sm">
                {LANGS.map(({ key, label: langLabel }) => (
                  <p key={key}>
                    <strong>{langLabel}:</strong>{" "}
                    {detailsPromotion.translations[key] || (
                      <span className="italic text-neutral-400">
                        {t("admin.licencePromotions.details.noTranslation")}
                      </span>
                    )}
                  </p>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase text-neutral-400">
                  {t("admin.licencePromotions.details.assignedFacilities")}
                </p>
                <Button
                  size="sm"
                  onClick={() => openAssignModal(detailsPromotion)}
                  disabled={detailsPromotion.promotionId === undefined}
                  className="flex items-center space-x-1"
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>{t("admin.licencePromotions.assign.title")}</span>
                </Button>
              </div>

              {detailsLoading ? (
                <div className="py-4 text-sm text-center text-neutral-500">
                  {t("common.loading")}
                </div>
              ) : assignedFacilities.length === 0 ? (
                <p className="text-sm italic text-neutral-400">
                  {t("admin.licencePromotions.details.noFacilities")}
                </p>
              ) : (
                <ul className="overflow-hidden border divide-y rounded-lg divide-neutral-100 border-neutral-200">
                  {assignedFacilities.map(a => (
                    <li
                      key={a.facilityId}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span>{a.facilityName}</span>
                      <button
                        onClick={() => handleUnassignFromDetails(a.facilityId)}
                        disabled={unassigningFacilityId === a.facilityId}
                        className="p-1 transition-colors rounded text-neutral-500 hover:bg-danger-50 hover:text-danger-600 disabled:opacity-50"
                        title={t(
                          "admin.licencePromotions.details.removeFromFacility"
                        )}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end pt-2 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={() => setDetailsPromotion(null)}
              >
                {t("common.close")}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Modal */}
      <Modal
        isOpen={assignTarget !== null}
        onClose={closeAssignModal}
        size="2xl"
        title={t("admin.licencePromotions.assign.title")}
      >
        {assignTarget && (
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-neutral-700">
                {t("admin.licencePromotions.assign.facilityLabel")}
              </label>
              {assignFacilityValue === ALL_FACILITIES_VALUE ? (
                <div className="flex items-center justify-between p-3 border rounded-md border-neutral-200 bg-neutral-50">
                  <span className="text-sm text-neutral-800">
                    {t("admin.licencePromotions.assign.allLicensedFacilities", {
                      count: licensedFacilityIds.length,
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAssignFacilityValue("");
                      setAssignError(null);
                    }}
                    className="text-xs font-medium text-ews-primary hover:underline"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              ) : (
                <>
                  <SearchAutocomplete<FacilitySearchEntity>
                    selectedId={assignFacilityValue}
                    onSelect={id => {
                      setAssignFacilityValue(id);
                      setAssignError(null);
                    }}
                    searchFunction={searchFacilitiesForAssign}
                    getDisplayValue={f => f.name}
                    placeholder={t(
                      "admin.licencePromotions.assign.facilityPlaceholder"
                    )}
                  />
                  {/* MED-1540: say how many facilities this covers, and leave
                      the action out while the licensed list is unknown or empty
                      — it used to promise "all 100 facilities", a number that
                      was only the directory search's page size. */}
                  {licensedFacilitiesLoading ? (
                    <p className="mt-1 text-xs text-neutral-400">
                      {t("common.loading")}
                    </p>
                  ) : licensedFacilityIds.length === 0 ? (
                    <p className="mt-1 text-xs text-neutral-500">
                      {t("admin.licencePromotions.assign.noLicensedFacilities")}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setAssignFacilityValue(ALL_FACILITIES_VALUE);
                        setAssignError(null);
                      }}
                      className="mt-1 text-xs font-medium text-ews-primary hover:underline"
                    >
                      {t(
                        "admin.licencePromotions.assign.allLicensedFacilities",
                        {
                          count: licensedFacilityIds.length,
                        }
                      )}
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 p-3 border rounded-md bg-neutral-50 border-neutral-200">
              <div>
                <p className="text-xs font-medium text-neutral-500 mb-0.5">
                  {t("admin.licencePromotions.table.startDate")}
                </p>
                <p className="text-sm text-neutral-800">
                  {formatDateTime(assignTarget.startDate)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 mb-0.5">
                  {t("admin.licencePromotions.table.endDate")}
                </p>
                <p className="text-sm text-neutral-800">
                  {formatDateTime(assignTarget.endDate)}
                </p>
              </div>
            </div>
            <p className="text-xs text-neutral-400">
              {t("admin.licencePromotions.assign.datesInheritedNote")}
            </p>

            {assignError && (
              <div className="flex items-start gap-2 p-3 text-sm border rounded-md bg-danger-50 border-danger-200 text-danger-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{assignError}</span>
              </div>
            )}

            <div className="flex justify-end pt-2 space-x-3 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={closeAssignModal}
                disabled={assigning}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleAssignSubmit}
                loading={assigning}
                disabled={assigning || !assignFacilityValue}
              >
                {t("common.confirm")}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm assign-to-all */}
      <ConfirmationModal
        isOpen={confirmAssignAll}
        onClose={() => setConfirmAssignAll(false)}
        onConfirm={() => {
          setConfirmAssignAll(false);
          void handleAssignConfirm();
        }}
        title={t("admin.licencePromotions.assign.confirmAllTitle")}
        message={t("admin.licencePromotions.assign.confirmAllMessage", {
          count: licensedFacilityIds.length,
        })}
        variant="warning"
        confirmLabel={t("common.confirm")}
      />

      {/* Delete (whole promotion code) confirmation */}
      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={t("admin.licencePromotions.delete.confirmTitle")}
        message={t("admin.licencePromotions.delete.confirmMessage", {
          code: deleteTarget?.promotionCode ?? "",
        })}
        variant="danger"
        isLoading={deleting}
        confirmLabel={t("common.delete")}
      />
    </div>
  );
};
